import React, { useEffect, useState } from "react";
import { makeApi, URLS } from "Utils/apiURL";

// Mirrors Components/PreviewPopup/ContactAutoCompleteDropdown.js — same
// debounce/cancel shape, pointed at the account search endpoint instead.
let accountsListNetworkRequest = {
  fetchAccounts: null
};

const setAccountsListNetworkRequest = (id = "") => {
  if (id && accountsListNetworkRequest?.[id] !== null) {
    accountsListNetworkRequest[id]("cancel");
    accountsListNetworkRequest[id] = null;
  }
};

const cancelReqCallback = (id = "", value = "") => {
  id && (accountsListNetworkRequest[id] = value);
};

const AccountAutoCompleteDropdown = ({
  inputText = "",
  selectedCallbk = () => {},
  focusTrigger = 0
}) => {
  const [accountList, setAccountList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [isInit, setIsInit] = useState(true);

  const changeAction = (obj = {}) => {
    setShowDropdown(false);
    setKeyword(obj?.name || obj?.domain || "");
    selectedCallbk(obj);
  };

  // Best-match-first: an exact prefix match on name/domain should surface above
  // other hits for the same keyword, instead of relying only on the backend's
  // relevance score — stable otherwise (keeps the backend's own ordering as the
  // tiebreaker).
  const getSortedAccountList = () => {
    const kw = keyword?.trim()?.toLowerCase();
    if (!kw) return accountList;
    const rank = obj => {
      const name = (obj?.name || "").toLowerCase();
      const domain = (obj?.domain || "").toLowerCase();
      if (name === kw || domain === kw) return 0;
      if (name.startsWith(kw) || domain.startsWith(kw)) return 1;
      return 2;
    };
    return [...accountList]
      .map((item, idx) => ({ item, idx, rank: rank(item) }))
      .sort((a, b) => a.rank - b.rank || a.idx - b.idx)
      .map(({ item }) => item);
  };

  const renderItem = (obj = {}) => {
    const { id = "", name = "", domain = "" } = obj || {};
    return (
      <li
        key={id}
        id={id}
        className="option"
        onClick={() => changeAction(obj)}
      >
        <div className="listItem">
          <div className="listItemWrap">
            <span className="itemName">{name || domain}</span>
            {name && domain && <span className="itemEmail">{domain}</span>}
          </div>
        </div>
      </li>
    );
  };

  const getAccountsByKeywordSearch = async (value = "") => {
    if (value && keyword?.toLowerCase() !== value?.toLowerCase()) {
      const config = {
        url: URLS.getAccountList,
        method: "POST",
        data: { searchTerm: value, pageNo: 1, limit: 10 }
      };
      setAccountsListNetworkRequest("fetchAccounts");
      const cancel = value => cancelReqCallback("fetchAccounts", value);
      let res = await makeApi(config, cancel);
      if (
        res?.message?.message?.toLowerCase() === "cancel" ||
        res?.message?.toLowerCase() === "cancel"
      ) {
        console.error("Request cancelled");
      } else {
        if (+res?.status === 200) {
          setAccountList(res?.data?.accounts || []);
          setKeyword(value);
          setShowDropdown(true);
        }
      }
    }
  };

  // Clicking/focusing the (still empty) input shows a starting list instead of
  // requiring the user to type something first.
  const getDefaultAccounts = async () => {
    const config = {
      url: URLS.getAccountList,
      method: "POST",
      data: { pageNo: 1, limit: 10 }
    };
    const res = await makeApi(config);
    if (+res?.status === 200) {
      setAccountList(res?.data?.accounts || []);
      setKeyword("");
      setShowDropdown(true);
    }
  };

  const closeAction = () => {
    setShowDropdown(false);
    setKeyword("");
    setAccountList([]);
  };

  useEffect(() => {
    if (inputText && !isInit) {
      getAccountsByKeywordSearch(inputText);
    } else {
      setAccountList([]);
    }
    !inputText && showDropdown && setShowDropdown(false);
    setIsInit(false);
    setKeyword(inputText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputText]);

  useEffect(() => {
    if (focusTrigger > 0 && !inputText) {
      getDefaultAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTrigger]);

  useEffect(() => {
    return () => {
      setIsInit(true);
    };
  }, []);

  return (
    showDropdown && (
      <div className="accountAutoCompleteDropdown">
        <div className="accountDropdownPanel animatedFast animatedDropDown">
          <ul className="dropdownList">
            {accountList?.length === 0 && (
              <li className="option noOption">No accounts found</li>
            )}
            {accountList?.length > 0 &&
              getSortedAccountList().map(item => renderItem(item))}
          </ul>
        </div>
        <div className="dropdownMask" onClick={closeAction} />
      </div>
    )
  );
};

export default AccountAutoCompleteDropdown;
export { AccountAutoCompleteDropdown };
